import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardText,
  ShieldCheck,
  Upload,
  CurrencyInr,
  PaperPlaneTilt,
  IdentificationBadge,
  CheckCircle,
  Clock,
  Warning,
} from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { fmtDate } from "@/lib/format";
import RegistrationDetailDrawer from "@/components/RegistrationDetailDrawer";
import BranchFilter from "@/components/BranchFilter";
import { useAuth } from "@/context/AuthContext";

const STAGE_ICONS = {
  kyc: IdentificationBadge,
  insurance: ShieldCheck,
  vahan: Upload,
  payment: CurrencyInr,
  docs: PaperPlaneTilt,
};

const PAGE_CONFIG = {
  rto: {
    title: "Registrations",
    subtitle: "RTO pipeline — KYC, insurance, Vahan, registration payment, document handover",
    permPrefix: "registrations",
    showFinancePending: true,
  },
  post_sale: {
    title: "Post-Sale Tasks",
    subtitle: "DMS, PDI queries, customer feedback, 15-day service & renewal reminders",
    permPrefix: "post_sale",
    showFinancePending: false,
  },
};

// Age helpers — compute days between started_at and now.
const ageDays = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const ageBadge = (started, slaSum) => {
  const days = ageDays(started);
  if (days <= slaSum) {
    return { cls: "border-[#00B341] text-[#00713a] bg-[#00B341]/10", label: `${days}d · on track` };
  }
  if (days <= slaSum * 2) {
    return { cls: "border-amber-500 text-amber-700 bg-amber-50", label: `${days}d · slipping` };
  }
  return { cls: "border-destructive text-destructive bg-destructive/10", label: `${days}d · overdue` };
};

export default function RegistrationsPage({ phase = "rto" } = {}) {
  const { user } = useAuth();
  const cfg = PAGE_CONFIG[phase] || PAGE_CONFIG.rto;
  const canEdit =
    user?.role === "admin" || (user?.permissions || []).includes(`${cfg.permPrefix}.edit`);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("in_progress");
  const [branchFilter, setBranchFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Debounce the search box — avoids one API call per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  // Completed-tab date filter. Default: last 30 days — keeps the load
  // snappy even when there are thousands of historical closed regs.
  const _todayIso = () => new Date().toISOString().slice(0, 10);
  const _daysAgoIso = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const [completedFrom, setCompletedFrom] = useState(() => _daysAgoIso(30));
  const [completedTo, setCompletedTo] = useState(_todayIso);
  // Separate started-at range for the In-Progress tab. Undefined by
  // default (shows the full backlog) so ops can decide whether to
  // scope by "started today" or see the whole pipeline.
  const [startedFrom, setStartedFrom] = useState("");
  const [startedTo, setStartedTo] = useState("");
  const [includeImported, setIncludeImported] = useState(false);
  const [financePending, setFinancePending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { phase };
      if (status && status !== "finance_pending") params.status = status;
      if (branchFilter) params.branch_id = branchFilter;
      if (search) params.q = search;
      if (includeImported) params.include_imported = true;
      if (status === "completed") {
        if (completedFrom) params.completed_from = completedFrom;
        if (completedTo) params.completed_to = completedTo;
      }
      if (status === "in_progress") {
        if (startedFrom) params.started_from = startedFrom;
        if (startedTo) params.started_to = startedTo;
      }
      const sumParams = { phase };
      if (branchFilter) sumParams.branch_id = branchFilter;
      if (includeImported) sumParams.include_imported = true;
      if (status === "completed") {
        if (completedFrom) sumParams.completed_from = completedFrom;
        if (completedTo) sumParams.completed_to = completedTo;
      }
      if (status === "in_progress") {
        if (startedFrom) sumParams.started_from = startedFrom;
        if (startedTo) sumParams.started_to = startedTo;
      }
      const sumPromise = api.get("/registrations/summary/dashboard", { params: sumParams });
      const fpPromise = cfg.showFinancePending
        ? api.get("/registrations/reports/finance-pending", { params: branchFilter ? { branch_id: branchFilter } : {} })
        : Promise.resolve({ data: { rows: [] } });
      const [{ data }, { data: sum }, { data: fp }] = await Promise.all([
        status === "finance_pending"
          ? Promise.resolve({ data: [] })
          : api.get("/registrations", { params }),
        sumPromise,
        fpPromise,
      ]);
      setRows(data || []);
      setSummary(sum);
      setFinancePending(fp?.rows || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [status, phase, cfg.showFinancePending, branchFilter, completedFrom, completedTo, search, includeImported, startedFrom, startedTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="px-6 py-6">
      <PageHeader
        title={cfg.title}
        subtitle={cfg.subtitle}
        eyebrow="Operations"
        actions={
          <div className="flex items-center gap-2">
            <BranchFilter value={branchFilter} onChange={setBranchFilter} testId="registrations-branch-filter" kind="sales" />
            {canEdit && phase === "rto" && (
              <>
                {user?.role === "admin" && (
                  <a
                    href="/admin/bulk-close"
                    data-testid="reg-bulk-cleanup-link"
                    className="px-3 py-2 border border-canvas-line text-ink-muted text-xs font-mono uppercase tracking-wider hover:border-ink hover:text-ink"
                    title="Admin — bulk-close pending Registration / Post-Sale backlog"
                  >
                    Bulk Cleanup
                  </a>
                )}
              </>
            )}
          </div>
        }
      />

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6" data-testid="reg-summary">
          <button
            onClick={() => setStatus("in_progress")}
            className={`text-left border-2 p-4 transition-colors ${status === "in_progress" ? "border-ink bg-canvas-card" : "border-canvas-line hover:border-ink"}`}
            data-testid="tile-in-progress"
          >
            <div className="flex items-center gap-2 mb-1">
              <Clock size={16} weight="bold" />
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">In Progress</div>
            </div>
            <div className="font-display text-3xl font-bold tabular-nums">{summary.in_progress}</div>
          </button>
          <button
            onClick={() => setStatus("in_progress")}
            className={`text-left border-2 p-4 ${summary.overdue > 0 ? "border-destructive bg-destructive/5" : "border-canvas-line"}`}
            data-testid="tile-overdue"
          >
            <div className="flex items-center gap-2 mb-1">
              <Warning size={16} weight="bold" className={summary.overdue > 0 ? "text-destructive" : ""} />
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Overdue</div>
            </div>
            <div className={`font-display text-3xl font-bold tabular-nums ${summary.overdue > 0 ? "text-destructive" : ""}`}>
              {summary.overdue}
            </div>
          </button>
          <button
            onClick={() => setStatus("completed")}
            className={`text-left border-2 p-4 transition-colors ${status === "completed" ? "border-ink bg-canvas-card" : "border-canvas-line hover:border-ink"}`}
            data-testid="tile-completed"
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={16} weight="bold" className="text-[#00713a]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                Completed{status === "completed" && (completedFrom || completedTo) ? " (in range)" : ""}
              </div>
            </div>
            <div className="font-display text-3xl font-bold tabular-nums">{summary.completed}</div>
          </button>
          {cfg.showFinancePending && (
            <button
              onClick={() => setStatus("finance_pending")}
              className={`text-left border-2 p-4 transition-colors ${status === "finance_pending" ? "border-[#B67100] bg-[#FFF6E5]" : "border-canvas-line hover:border-[#B67100]"}`}
              data-testid="tile-finance-pending"
            >
              <div className="flex items-center gap-2 mb-1">
                <Warning size={16} weight="bold" className="text-[#B67100]" />
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Finance Pending</div>
              </div>
              <div className="font-display text-3xl font-bold tabular-nums text-[#B67100]">{financePending.length}</div>
            </button>
          )}
        </div>
      )}

      {/* Top-level filter bar — VIN / mobile search + Completed date range */}
      <div className="flex items-center gap-3 flex-wrap mb-4 px-1" data-testid="reg-filter-bar">
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <label className="font-mono text-[10px] uppercase tracking-wider text-ink-muted whitespace-nowrap">Search</label>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="VIN / Chassis / Mobile / Customer name"
            data-testid="reg-search-input"
            className="flex-1 min-w-[220px] px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:border-ink focus:outline-none"
          />
          {search && (
            <button
              onClick={() => { setSearchInput(""); setSearch(""); }}
              data-testid="reg-search-clear"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {status === "in_progress" && (
          <div className="flex items-center gap-2" data-testid="reg-started-daterange">
            <label className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Started</label>
            <input
              type="date"
              value={startedFrom}
              onChange={(e) => setStartedFrom(e.target.value)}
              data-testid="reg-started-from"
              className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono"
              title="Started from"
            />
            <span className="text-ink-muted text-xs">→</span>
            <input
              type="date"
              value={startedTo}
              onChange={(e) => setStartedTo(e.target.value)}
              data-testid="reg-started-to"
              className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono"
              title="Started to"
            />
            <button
              onClick={() => { setStartedFrom(_todayIso()); setStartedTo(_todayIso()); }}
              data-testid="reg-started-preset-today"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
              title="Only regs started today"
            >
              Today
            </button>
            <button
              onClick={() => { setStartedFrom(_daysAgoIso(7)); setStartedTo(_todayIso()); }}
              data-testid="reg-started-preset-7d"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
            >
              7d
            </button>
            <button
              onClick={() => { setStartedFrom(""); setStartedTo(""); }}
              data-testid="reg-started-preset-all"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
              title="Full backlog"
            >
              All
            </button>
          </div>
        )}
        {status === "completed" && (
          <div className="flex items-center gap-2" data-testid="reg-completed-daterange">
            <label className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Completed</label>
            <input
              type="date"
              value={completedFrom}
              onChange={(e) => setCompletedFrom(e.target.value)}
              data-testid="reg-completed-from"
              className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono"
              title="Completed from"
            />
            <span className="text-ink-muted text-xs">→</span>
            <input
              type="date"
              value={completedTo}
              onChange={(e) => setCompletedTo(e.target.value)}
              data-testid="reg-completed-to"
              className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono"
              title="Completed to"
            />
            <button
              onClick={() => { setCompletedFrom(_todayIso()); setCompletedTo(_todayIso()); }}
              data-testid="reg-completed-preset-today"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
              title="Today only"
            >
              Today
            </button>
            <button
              onClick={() => { setCompletedFrom(_daysAgoIso(30)); setCompletedTo(_todayIso()); }}
              data-testid="reg-completed-preset-30d"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
              title="Last 30 days"
            >
              30d
            </button>
            <button
              onClick={() => { setCompletedFrom(_daysAgoIso(90)); setCompletedTo(_todayIso()); }}
              data-testid="reg-completed-preset-90d"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
              title="Last 90 days"
            >
              90d
            </button>
            <button
              onClick={() => { setCompletedFrom(""); setCompletedTo(""); }}
              data-testid="reg-completed-preset-all"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
              title="All time (may be slow on large datasets)"
            >
              All
            </button>
          </div>
        )}
        <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-muted cursor-pointer hover:text-ink" title="Include ~4500 migrated legacy regs (bulk-imported from your prior system). Off by default — they inflate today's counts.">
          <input
            type="checkbox"
            checked={includeImported}
            onChange={(e) => setIncludeImported(e.target.checked)}
            data-testid="reg-include-imported"
            className="w-3.5 h-3.5"
          />
          Show migrated
        </label>
      </div>

      <div className="border-2 border-ink">
        <div className="px-4 py-3 border-b border-canvas-line flex items-center gap-3 flex-wrap">
          <ClipboardText size={16} weight="bold" />
          <h2 className="font-display font-semibold">
            {status === "finance_pending"
              ? "Finance-Pending Registrations"
              : status === "completed"
                ? "Completed Registrations"
                : "Pipeline (In Progress)"}
          </h2>
          <span className="font-mono text-[11px] text-ink-muted ml-auto">
            {loading ? "Loading…" : status === "finance_pending"
              ? `${financePending.length} stuck`
              : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {status === "finance_pending" ? (
          financePending.length === 0 ? (
            <div className="p-10 text-center text-ink-muted text-sm">No finance-pending registrations. Every finance sale&apos;s RTO paperwork has cleared the financier check ✓</div>
          ) : (
            <table className="w-full text-sm" data-testid="finance-pending-table">
              <thead className="bg-canvas-panel">
                <tr>
                  {["Customer", "Vehicle", "Branch", "Financier", "Vahan Finance Thumb #", "Finance ₹", "Days Pending", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {financePending.map((r) => (
                  <tr key={r.registration_id} className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/50" data-testid={`finance-pending-row-${r.registration_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.customer_name || "—"}</div>
                      <div className="font-mono text-[11px] text-ink-muted">{r.customer_phone || ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[11px]">{r.vin || "—"}</div>
                      <div className="text-[12px] text-ink-muted">{r.model || ""}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-muted" data-testid={`finance-pending-branch-${r.registration_id}`}>{r.branch_name || "—"}</td>
                    <td className="px-3 py-2">{r.financier_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {r.finance_thumb_no ? (
                        <span className="text-ink">{r.finance_thumb_no}</span>
                      ) : (
                        <span className="text-destructive" title="Vahan finance thumb number not captured — chase the RTO team">
                          {r.application_no}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono tabular-nums">{Number(r.finance_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${r.days_pending > 14 ? "border-destructive text-destructive" : r.days_pending > 7 ? "border-[#B67100] text-[#B67100]" : "border-canvas-line"}`}>
                        {r.days_pending}d
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setSelectedId(r.registration_id)} className="text-[#1971c2] text-xs font-mono uppercase tracking-wider hover:underline" data-testid={`finance-pending-open-${r.registration_id}`}>Open →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-ink-muted text-sm">
            {status === "completed" ? (
              (completedFrom || completedTo || search) ? (
                <div className="space-y-3">
                  <div>
                    No registrations completed
                    {completedFrom || completedTo ? (
                      <> between <b>{completedFrom || "…"}</b> and <b>{completedTo || "today"}</b></>
                    ) : null}
                    {search ? <> matching &quot;<b>{search}</b>&quot;</> : null}.
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => { setCompletedFrom(_daysAgoIso(90)); setCompletedTo(_todayIso()); }}
                      data-testid="reg-empty-widen-90d"
                      className="px-3 py-1.5 border border-canvas-line text-[11px] font-mono uppercase tracking-wider hover:border-ink hover:text-ink"
                    >
                      Try last 90 days
                    </button>
                    <button
                      onClick={() => { setCompletedFrom(""); setCompletedTo(""); if (search) { setSearch(""); setSearchInput(""); } }}
                      data-testid="reg-empty-show-all"
                      className="px-3 py-1.5 border border-ink text-[11px] font-mono uppercase tracking-wider hover:bg-ink hover:text-white"
                    >
                      Show All
                    </button>
                  </div>
                </div>
              ) : "No registrations completed yet."
            ) : (search
                ? <>No registrations match &quot;<b>{search}</b>&quot;. <button onClick={() => { setSearch(""); setSearchInput(""); }} className="ml-2 underline">Clear search</button></>
                : "No active registrations. They auto-start as soon as accounts approves a sale — dues can be collected later."
              )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-canvas-panel">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Customer</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Vehicle</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Branch</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Started</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Stages (Registration · Post-Sale)</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Age / SLA</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const slaSum = (r.stages || []).reduce((s, x) => s + (x.sla_days || 0), 0);
                const badge = ageBadge(r.started_at, slaSum);
                return (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedId(r.id)}
                    data-testid={`reg-row-${r.id}`}
                    className="border-t border-canvas-line cursor-pointer hover:bg-canvas-panel"
                  >
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.customer_name || "—"}</div>
                      <div className="font-mono text-[10px] text-ink-muted">{r.customer_phone || ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs">{r.vehicle_model || "—"}</div>
                      <div className="font-mono text-[10px] text-ink-muted">{r.vehicle_vin}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-muted" data-testid={`reg-branch-${r.id}`}>{r.branch_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-muted">{fmtDate(r.started_at)}</td>
                    <td className="px-3 py-2">
                      {(() => {
                        // Strict per-team split: each page shows ONLY stages of its own phase.
                        // RTO page → registration stages. Post-Sale page → DMS/PDI/feedback/reminders.
                        // Legacy stages without `phase` default to "rto".
                        const ownStages = (r.stages || []).filter((s) => (s.phase || "rto") === phase);
                        const ownDone = ownStages.filter((s) => s.status === "done").length;
                        const renderIcon = (s) => {
                          const Icon = STAGE_ICONS[s.key] || ClipboardText;
                          const cls =
                            s.status === "done" ? "bg-[#00B341] text-white border-[#00B341]" :
                            s.status === "skipped" ? "bg-canvas-line text-ink-muted border-canvas-line" :
                            "bg-canvas border-canvas-line text-ink-muted";
                          return (
                            <span
                              key={s.key}
                              className={`inline-flex items-center justify-center w-6 h-6 border ${cls}`}
                              title={`${s.label} — ${s.status}`}
                            >
                              <Icon size={11} weight="bold" />
                            </span>
                          );
                        };
                        return (
                          <div className="flex items-center gap-3 flex-wrap" data-testid={`stages-cell-${r.id}`}>
                            <div className="flex items-center gap-1">
                              {ownStages.length === 0
                                ? <span className="font-mono text-[10px] text-ink-muted">—</span>
                                : ownStages.map(renderIcon)}
                            </div>
                            <span className="font-mono text-[10px] text-ink-muted self-end">
                              {ownDone}/{ownStages.length || 0}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] font-mono uppercase ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <RegistrationDetailDrawer
        registrationId={selectedId}
        onlyPhase={phase}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}
